import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Plus, X, CalendarDays, Trash2, Clock, User, Banknote, StickyNote, Tag, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { addEvent, removeEvent, type ManagerEvent } from "@/lib/managerStore";
import { managerApi, type ManagerBookingSummary } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/calendar")({
  component: CalendarPage,
});

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* ── type styling ── */
const TYPE_META: Record<string, { pill: string; dot: string; badge: string; label: string }> = {
  booking:     { pill: "bg-primary/10 text-primary",          dot: "bg-primary",       badge: "border-l-[3px] border-primary bg-primary/10 text-primary",           label: "Guest Booking"   },
  event:       { pill: "bg-violet-100 text-violet-700",        dot: "bg-violet-500",    badge: "border-l-[3px] border-violet-500 bg-violet-50 text-violet-700",       label: "Venue Event"     },
  blocked:     { pill: "bg-rose-100 text-rose-700",            dot: "bg-rose-400",      badge: "border-l-[3px] border-rose-400 bg-rose-50 text-rose-700",             label: "Blocked"         },
  maintenance: { pill: "bg-amber-100 text-amber-700",          dot: "bg-amber-400",     badge: "border-l-[3px] border-amber-400 bg-amber-50 text-amber-700",          label: "Maintenance"     },
};
const STATUS_CHIP: Record<string, string> = {
  confirmed: "bg-emerald-100 text-emerald-700",
  pending:   "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-600",
};

type FormState = { title: string; date: string; time: string; type: string; guest: string; amount: string; notes: string; status: string };
const blankForm = (): FormState => ({ title: "", date: "", time: "12:00", type: "booking", guest: "", amount: "", notes: "", status: "confirmed" });

function pad2(n: number) { return String(n).padStart(2, "0"); }
function ymd(y: number, m: number, d: number) { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }
const INP = "h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/25 transition";

function mapBookingsToEvents(bookings: ManagerBookingSummary[]): ManagerEvent[] {
  return bookings.map(b => ({
    id: b.bookingId,
    title: `${b.venueName} — ${b.guestName}`,
    date: b.startDate.slice(0, 10),
    endDate: b.endDate.slice(0, 10),
    time: "14:00",
    type: "booking" as ManagerEvent["type"],
    guest: b.guestName,
    amount: Number(b.totalAmount),
    notes: `Ref: ${b.bookingRef} · ${b.daysCount} night${b.daysCount !== 1 ? "s" : ""}`,
    status: b.status.toLowerCase() as ManagerEvent["status"],
    createdAt: b.createdAt,
    nights: b.daysCount,
  }));
}

export default function CalendarPage() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth());
  const [events, setEvents] = useState<ManagerEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string>(now.toISOString().slice(0, 10));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]     = useState<FormState>(blankForm());
  const [peek, setPeek]     = useState<ManagerEvent | null>(null);

  const reload = useCallback(() => {
    if (!user?.id) return;
    setLoading(true);
    managerApi.dashboard(user.id)
      .then(res => setEvents(mapBookingsToEvents(res.recentBookings)))
      .catch(() => toast.error("Failed to load calendar data"))
      .finally(() => setLoading(false));
  }, [user?.id]);
  useEffect(() => { reload(); }, [reload]);

  const { grid, byDate } = useMemo(() => {
    const first = new Date(year, month, 1).getDay();
    const last  = new Date(year, month + 1, 0).getDate();
    const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: last }, (_, i) => i + 1)];
    while (cells.length < 42) cells.push(null);
    const byDate: Record<string, ManagerEvent[]> = {};
    for (const ev of events) { (byDate[ev.date] ??= []).push(ev); }
    return { grid: cells, byDate };
  }, [year, month, events]);

  const selectedEvents = byDate[selected] ?? [];
  const todayStr = now.toISOString().slice(0, 10);

  const prevMonth = () => month === 0 ? (setYear(y => y - 1), setMonth(11)) : setMonth(m => m - 1);
  const nextMonth = () => month === 11 ? (setYear(y => y + 1), setMonth(0)) : setMonth(m => m + 1);
  const goToday   = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelected(todayStr); };

  const openForm = (date = selected) => { setForm({ ...blankForm(), date }); setShowForm(true); };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) return toast.error("Title and date are required");
    if (!user?.id) return;
    const newEvent: ManagerEvent = {
      id: crypto.randomUUID(), title: form.title.trim(), date: form.date, time: form.time,
      type: form.type as ManagerEvent["type"], guest: form.guest.trim() || undefined,
      notes: form.notes.trim() || undefined, amount: form.amount ? parseFloat(form.amount) : undefined,
      status: form.status as ManagerEvent["status"], createdAt: new Date().toISOString(),
    };
    addEvent(user.id, newEvent);
    setEvents(prev => [...prev, newEvent]);
    setShowForm(false); toast.success("Event saved");
  };

  const del = (id: string) => {
    if (!user?.id || !confirm("Delete this event?")) return;
    removeEvent(user.id, id);
    setEvents(prev => prev.filter(e => e.id !== id));
    setPeek(null); toast.success("Deleted");
  };

  /* ── Legend ── */
  const legend = Object.entries(TYPE_META).map(([k, v]) => ({ key: k, ...v }));
  /* ── month event count for mini badge ── */
  const monthPrefix = `${year}-${pad2(month + 1)}`;
  const monthCount = events.filter(e => e.date.startsWith(monthPrefix)).length;

  return (
    <div className="flex h-full min-h-screen flex-col bg-background p-4 sm:p-6 lg:p-8 gap-6">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {loading ? "Loading bookings…" : monthCount > 0 ? `${monthCount} event${monthCount > 1 ? "s" : ""} in ${MONTHS[month]}` : "No bookings this month"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToday} className="rounded-full">Today</Button>
          <Button size="sm" onClick={() => openForm()} className="gap-2 rounded-full">
            <Plus className="size-4" /> New Event
          </Button>
        </div>
      </div>

      {/* ── Main two-pane layout ── */}
      <div className="grid gap-4 md:grid-cols-[1fr_280px] lg:grid-cols-[1fr_320px] flex-1">

        {/* ── Calendar card ── */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden flex flex-col">

          {/* month nav */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50">
            <button onClick={prevMonth} className="grid size-8 place-items-center rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
              <ChevronLeft className="size-4" />
            </button>
            <div className="flex-1 text-center">
              <span className="text-base font-bold">{MONTHS[month]}</span>
              <span className="ml-2 text-base font-normal text-muted-foreground">{year}</span>
            </div>
            <button onClick={nextMonth} className="grid size-8 place-items-center rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
              <ChevronRight className="size-4" />
            </button>
          </div>

          {/* day-of-week headers */}
          <div className="grid grid-cols-7 border-b border-border/40 bg-accent/30">
            {DAYS_SHORT.map(d => (
              <div key={d} className="py-2.5 text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{d}</div>
            ))}
          </div>

          {/* grid */}
          <div className="grid grid-cols-7 flex-1 divide-x divide-y divide-border/25">
            {grid.map((day, i) => {
              const ds = day ? ymd(year, month, day) : null;
              const evs = ds ? (byDate[ds] ?? []) : [];
              const isToday = ds === todayStr;
              const isSel   = ds === selected;
              const isOther = !day;

              return (
                <div
                  key={i}
                  onClick={() => ds && setSelected(ds)}
                  className={[
                    "relative min-h-[88px] cursor-pointer p-2 transition-colors select-none group",
                    isOther ? "bg-accent/15 cursor-default" : "hover:bg-accent/30",
                    isSel   ? "bg-primary/5 ring-1 ring-inset ring-primary/40" : "",
                  ].join(" ")}
                >
                  {day && (
                    <>
                      {/* day number */}
                      <span className={[
                        "inline-flex size-7 items-center justify-center rounded-full text-[13px] font-semibold transition-colors mb-1",
                        isToday ? "bg-primary text-primary-foreground shadow-sm" :
                        isSel   ? "bg-primary/15 text-primary" : "text-foreground group-hover:bg-accent",
                      ].join(" ")}>
                        {day}
                      </span>

                      {/* event pills */}
                      <div className="space-y-[3px]">
                        {evs.slice(0, 2).map(ev => (
                          <button
                            key={ev.id}
                            onClick={e => { e.stopPropagation(); setPeek(ev); }}
                            className={`w-full truncate rounded-md px-1.5 py-[2px] text-left text-[10px] font-semibold leading-tight ${TYPE_META[ev.type]?.badge ?? "bg-muted text-foreground"}`}
                          >
                            {ev.title}
                          </button>
                        ))}
                        {evs.length > 2 && (
                          <p className="pl-1 text-[10px] text-muted-foreground font-medium">+{evs.length - 2}</p>
                        )}
                      </div>

                      {/* dots for many events */}
                      {evs.length > 0 && evs.length <= 2 && (
                        <div className="absolute bottom-1.5 right-2 flex gap-0.5">
                          {evs.map(ev => (
                            <span key={ev.id} className={`size-1.5 rounded-full ${TYPE_META[ev.type]?.dot ?? "bg-muted-foreground"}`} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* legend */}
          <div className="border-t border-border/40 px-5 py-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {legend.map(l => (
              <span key={l.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={`size-2 rounded-full ${l.dot}`} />
                {l.label}
              </span>
            ))}
          </div>
        </div>

        {/* ── Right panel: selected day ── */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft flex flex-col overflow-hidden">
          {/* panel header */}
          <div className="border-b border-border/50 px-5 py-4 flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                {selected === todayStr ? "Today" : new Date(selected + "T00:00:00").toLocaleDateString("en-NG", { weekday: "long" })}
              </p>
              <h2 className="text-lg font-extrabold leading-tight">
                {new Date(selected + "T00:00:00").toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}
              </h2>
            </div>
            <Button size="sm" onClick={() => openForm(selected)} className="shrink-0 gap-1 rounded-full text-xs h-8 px-3">
              <Plus className="size-3" /> Add
            </Button>
          </div>

          {/* events list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {selectedEvents.length === 0 ? (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-center">
                <CalendarDays className="size-9 text-muted-foreground/30" />
                <p className="text-sm font-medium text-muted-foreground">No events</p>
                <button onClick={() => openForm(selected)} className="text-xs text-primary hover:underline">
                  + Create one
                </button>
              </div>
            ) : (
              selectedEvents.map(ev => (
                <button
                  key={ev.id}
                  onClick={() => setPeek(ev)}
                  className={`w-full text-left rounded-xl p-3 transition-all hover:scale-[1.01] ${TYPE_META[ev.type]?.badge ?? "bg-muted"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm leading-snug">{ev.title}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${STATUS_CHIP[ev.status] ?? "bg-muted"}`}>
                      {ev.status}
                    </span>
                  </div>
                  <p className="mt-1 flex items-center gap-1 text-xs opacity-70">
                    <Clock className="size-3" /> {ev.time} · {TYPE_META[ev.type]?.label}
                  </p>
                  {ev.guest && <p className="mt-0.5 flex items-center gap-1 text-xs opacity-70"><User className="size-3" /> {ev.guest}</p>}
                  {ev.amount && <p className="mt-0.5 flex items-center gap-1 text-xs opacity-70"><Banknote className="size-3" /> ₦{ev.amount.toLocaleString()}</p>}
                </button>
              ))
            )}
          </div>

          {/* upcoming mini list */}
          {events.filter(e => e.date > selected).length > 0 && (
            <div className="border-t border-border/40 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Coming up</p>
              <div className="space-y-1">
                {events.filter(e => e.date > selected).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 3).map(ev => (
                  <button key={ev.id} onClick={() => { setSelected(ev.date); setPeek(ev); }}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-accent transition-colors">
                    <span className={`size-2 shrink-0 rounded-full ${TYPE_META[ev.type]?.dot ?? "bg-muted-foreground"}`} />
                    <span className="flex-1 truncate text-xs font-medium">{ev.title}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{ev.date.slice(5)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Event peek drawer ── */}
      {peek && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={() => setPeek(null)} />
          <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-card shadow-2xl border-t border-border lg:bottom-auto lg:left-auto lg:right-8 lg:top-1/2 lg:-translate-y-1/2 lg:w-[360px] lg:rounded-2xl lg:border">
            {/* handle */}
            <div className="flex justify-center pt-3 pb-1 lg:hidden">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <span className={`size-3 rounded-full ${TYPE_META[peek.type]?.dot ?? "bg-muted"}`} />
                <h2 className="font-bold text-base">{peek.title}</h2>
              </div>
              <button onClick={() => setPeek(null)} className="rounded-lg p-1.5 hover:bg-accent transition-colors">
                <X className="size-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex flex-wrap gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_CHIP[peek.status] ?? "bg-muted"}`}>{peek.status}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TYPE_META[peek.type]?.pill ?? "bg-muted"}`}>{TYPE_META[peek.type]?.label}</span>
              </div>
              {[
                { icon: CalendarDays, label: peek.date },
                { icon: Clock,       label: peek.time },
                { icon: User,        label: peek.guest },
                { icon: Banknote,    label: peek.amount ? `₦${peek.amount.toLocaleString()}` : undefined },
                { icon: Tag,         label: peek.nights ? `${peek.nights} nights` : undefined },
                { icon: StickyNote,  label: peek.notes },
              ].filter(r => r.label).map((r, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <r.icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <span>{r.label}</span>
                </div>
              ))}
            </div>
            <div className="px-5 pb-5">
              <Button variant="destructive" size="sm" className="w-full gap-2 rounded-xl" onClick={() => del(peek.id)}>
                <Trash2 className="size-4" /> Delete Event
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ── Create event modal ── */}
      {showForm && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border/60 bg-card p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-extrabold">New Event</h2>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-1.5 hover:bg-accent transition-colors"><X className="size-4" /></button>
            </div>
            <form onSubmit={save} className="space-y-3.5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold">Title <span className="text-rose-500">*</span></label>
                <input required className={INP} placeholder="e.g. Guest check-in / Team event" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold">Type</label>
                  <select className={INP} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold">Status</label>
                  <select className={INP} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold">Date <span className="text-rose-500">*</span></label>
                  <input required type="date" className={INP} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold">Time</label>
                  <input type="time" className={INP} value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
                </div>
              </div>

              {form.type === "booking" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold">Guest name</label>
                    <input className={INP} placeholder="Tunde Adebayo" value={form.guest} onChange={e => setForm(f => ({ ...f, guest: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold">Amount (₦)</label>
                    <input type="number" min="0" step="500" className={INP} placeholder="50000" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold">Notes</label>
                <textarea rows={2} className="w-full resize-none rounded-xl border border-border/70 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/25 transition" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 gap-1.5 rounded-xl"><Plus className="size-4" /> Save Event</Button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
